import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { wibToday } from "@/lib/bizdate";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading, EmptyState, StatusBadge } from "@/components/common";
import { numberID, formatDate } from "@/lib/format";
import { fmtWib } from "@/lib/bizdate";
import { downloadPdf } from "./PurchaseDetail";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Plus, PackagePlus, MoreVertical, X } from "lucide-react";
import CatchWeightIssuePanel from "./FabricRollIssue";

export default function MaterialRequests() {
  const { can } = useAuth();
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const canManage = can("production.material.issue");
  const isRoll = (u) => (u || "").toLowerCase() === "roll";
  const mrQtyDisplay = (mr) => {
    const its = mr.items || [];
    const total = its.reduce((s, it) => s + (Number(it.qty) || 0), 0);
    if (its.length && its.every((it) => isRoll(it.unit))) return `${numberID(total)} Roll`;
    return `${its.length} item`;
  };
  const load = () => { setRows(null); api.get("/production/material-requests").then((r) => setRows(r.data)); };
  useEffect(load, []);

  const issue = async (mr) => {
    try { await api.post(`/production/material-requests/${mr.id}/issue`); toast.success("Bahan dikeluarkan dari Konveksi"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const cancel = async (mr) => {
    try { await api.post(`/production/material-requests/${mr.id}/cancel`); toast.success("MR dibatalkan"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader title="Material Request" subtitle="Permintaan & pengeluaran bahan baku dari Inventory Konveksi untuk produksi." testId="mr-header">
        {canManage && <Button onClick={() => setOpen(true)} data-testid="add-mr-btn" className="bg-indigo-600 hover:bg-indigo-500"><Plus className="mr-1.5 h-4 w-4" /> Material Request</Button>}
      </PageHeader>
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-x-auto">
        {!rows ? <Loading /> : rows.length === 0 ? <EmptyState title="Belum ada Material Request" icon={PackagePlus} /> : (
          <Table>
            <TableHeader><TableRow><TableHead>No MR</TableHead><TableHead>Tanggal</TableHead><TableHead>Production Order</TableHead><TableHead className="text-right">Jumlah Bahan</TableHead><TableHead>Pemohon / Dibuat Oleh</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {rows.map((mr) => (
                <TableRow key={mr.id} data-testid={`mr-row-${mr.mr_no}`}>
                  <TableCell className="font-mono text-xs">{mr.mr_no}</TableCell>
                  <TableCell className="text-xs text-slate-500">{formatDate(mr.tanggal)}</TableCell>
                  <TableCell className="text-slate-500">{mr.po_prod_no || "-"}</TableCell>
                  <TableCell className="text-right" data-testid={`mr-qty-col-${mr.mr_no}`}>{mrQtyDisplay(mr)}</TableCell>
                  <TableCell className="text-xs text-slate-600" data-testid={`mr-creator-${mr.mr_no}`}>{mr.created_by_name || <span className="text-slate-400">Tidak tersedia</span>}</TableCell>
                  <TableCell><StatusBadge status={mr.status} /></TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="outline" size="sm" onClick={() => setDetail(mr)} data-testid={`mr-detail-${mr.mr_no}`}>Detail</Button>
                      {canManage && mr.status === "Draft" && (
                        <DropdownMenu><DropdownMenuTrigger asChild><Button variant="ghost" size="sm"><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => issue(mr)} data-testid={`mr-issue-${mr.mr_no}`}>Keluarkan Bahan</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => cancel(mr)} className="text-rose-600" data-testid={`mr-cancel-${mr.mr_no}`}>Batalkan</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
      {open && <MRDialog onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
      {detail && <MRDetailDialog mr={detail} onClose={() => setDetail(null)} />}

      {canManage && <CatchWeightIssuePanel />}
    </div>
  );
}

function MRDetailDialog({ mr, onClose }) {
  const isRoll = (u) => (u || "").toLowerCase() === "roll";
  const hasCw = (mr.items || []).some((it) => isRoll(it.unit));
  const [plan, setPlan] = useState(null);
  useEffect(() => {
    if (mr.production_order_id && hasCw) {
      api.get(`/production/production-orders/${mr.production_order_id}/fabric-plan`, { params: { material_request_id: mr.id } })
        .then((r) => setPlan(r.data)).catch(() => setPlan(null));
    }
  }, [mr.id]);
  const mrRollQty = (mr.items || []).filter((it) => isRoll(it.unit)).reduce((s, it) => s + (Number(it.qty) || 0), 0);
  const issued = plan?.mr?.effective_roll_usage ?? null;
  const Row = ({ k, v }) => (<div className="flex justify-between gap-4 py-1 text-sm"><span className="text-slate-500">{k}</span><span className="font-medium text-slate-800 text-right">{v}</span></div>);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="mr-detail-dialog">
        <DialogHeader><DialogTitle className="font-display">Detail Material Request · {mr.mr_no}</DialogTitle></DialogHeader>
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-2">
          <Row k="No MR" v={mr.mr_no} />
          <Row k="Status" v={<StatusBadge status={mr.status} />} />
          <Row k="Tanggal" v={formatDate(mr.tanggal)} />
          <Row k="Inventory Tujuan" v={mr.warehouse_name || "-"} />
          <Row k="Production Order" v={mr.po_prod_no || "Tanpa PO"} />
          <Row k="Pemohon / Dibuat Oleh" v={mr.created_by_name || <span className="text-slate-400">Tidak tersedia</span>} />
          {mr.created_at && <Row k="Dibuat" v={fmtWib(mr.created_at)} />}
          {mr.notes && <Row k="Catatan" v={mr.notes} />}
        </div>
        <div>
          <Label className="text-sm font-semibold">Daftar Bahan</Label>
          <div className="mt-2 rounded-xl border border-slate-200 overflow-hidden">
            <Table>
              <TableHeader><TableRow><TableHead>Bahan</TableHead><TableHead className="text-right">Qty</TableHead><TableHead>Unit</TableHead></TableRow></TableHeader>
              <TableBody>
                {(mr.items || []).map((it) => (
                  <TableRow key={it.id} data-testid={`mr-detail-line-${it.item_id}`}>
                    <TableCell className="text-sm">{it.item_name}{isRoll(it.unit) && <span className="ml-1.5 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">Catch Weight</span>}</TableCell>
                    <TableCell className="text-right font-semibold">{numberID(it.qty)}</TableCell>
                    <TableCell className="text-xs text-slate-500">{it.unit}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
        {plan && plan.planned_fabric_roll_qty != null && (
          <div className="rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-2" data-testid="mr-detail-cw">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-indigo-700">Catch Weight</p>
            <Row k="Planned Roll PO" v={`${numberID(plan.planned_fabric_roll_qty)} Roll`} />
            <Row k="Qty Roll MR" v={`${numberID(mrRollQty)} Roll`} />
            <Row k="Roll sudah di-issue (MR)" v={issued != null ? `${numberID(issued)} Roll` : "-"} />
            <Row k="Remaining MR quota" v={issued != null ? `${numberID(Math.max(0, mrRollQty - issued))} Roll` : "-"} />
            {(plan.mr?.fri_refs || []).length > 0 && <Row k="FRI terkait" v={(plan.mr.fri_refs).map((f) => f.fri_no).join(", ")} />}
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={() => downloadPdf(`/pdf/material-request/${mr.id}`)} data-testid="mr-detail-pdf">Cetak / Ekspor PDF</Button><Button variant="outline" onClick={onClose}>Tutup</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MRDialog({ onClose, onSaved }) {
  const today = wibToday();
  const [wh, setWh] = useState([]);
  const [pos, setPos] = useState([]);
  const [f, setF] = useState({ tanggal: today, warehouse_id: "", production_order_id: "", notes: "" });
  const [items, setItems] = useState([{ item_id: "", qty: 1 }]);
  const [whItems, setWhItems] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/inventory/warehouses").then((r) => {
      const konv = r.data.filter((w) => w.type === "KONVEKSI");
      setWh(konv);
      if (konv.length === 1) setF((p) => ({ ...p, warehouse_id: konv[0].id }));
    });
    api.get("/production/production-orders").then((r) => setPos(r.data.filter((p) => ["Disetujui", "Proses Produksi"].includes(p.status))));
  }, []);
  useEffect(() => {
    if (!f.warehouse_id) { setWhItems([]); return; }
    api.get("/inventory/items", { params: { warehouse_id: f.warehouse_id, status: "active" } }).then((r) => setWhItems(r.data));
  }, [f.warehouse_id]);

  const save = async () => {
    if (!f.warehouse_id) return toast.error("Pilih Inventory Konveksi");
    if (items.some((i) => !i.item_id || Number(i.qty) <= 0)) return toast.error("Lengkapi bahan & qty > 0");
    if (cwSelected && !f.production_order_id) return toast.error("Production Order wajib untuk Catch Weight");
    setSaving(true);
    const payload = {
      ...f, production_order_id: f.production_order_id || null,
      items: items.map((i) => ({ item_id: i.item_id, qty: Number(i.qty) })),
    };
    const submit = async (confirmLarge) => {
      try {
        await api.post("/production/material-requests", { ...payload, confirm_large: confirmLarge });
        toast.success("Material Request dibuat (Draft)"); onSaved();
      } catch (e) {
        const d = e?.response?.data?.detail || "";
        if (typeof d === "string" && d.startsWith("KONFIRMASI_QTY::")) {
          if (window.confirm(d.replace("KONFIRMASI_QTY::", ""))) return submit(true);
        } else { toast.error(apiError(e)); }
      }
    };
    await submit(false);
    setSaving(false);
  };
  const pickItem = (i, id) => setItems(items.map((x, idx) => idx === i ? { ...x, item_id: id } : x));
  const cwSelected = items.some((i) => whItems.find((x) => x.id === i.item_id)?.tracking_mode === "CATCH_WEIGHT_ROLL");

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">Material Request Baru</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs text-slate-500">Tanggal</Label><Input type="date" className="mt-1" value={f.tanggal} onChange={(e) => setF({ ...f, tanggal: e.target.value })} data-testid="mr-tanggal" /></div>
          <div><Label className="text-xs text-slate-500">Inventory Konveksi</Label><Select value={f.warehouse_id} onValueChange={(v) => setF({ ...f, warehouse_id: v })}><SelectTrigger className="mt-1" data-testid="mr-warehouse"><SelectValue placeholder="Pilih..." /></SelectTrigger><SelectContent>{wh.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent></Select></div>
          <div className="col-span-2"><Label className="text-xs text-slate-500">Production Order {cwSelected ? <span className="text-rose-600">(wajib untuk Catch Weight)</span> : "(opsional)"}</Label><Select value={f.production_order_id} onValueChange={(v) => setF({ ...f, production_order_id: v })}><SelectTrigger className="mt-1" data-testid="mr-po"><SelectValue placeholder="Tanpa PO / Pilih PO" /></SelectTrigger><SelectContent>{pos.map((p) => <SelectItem key={p.id} value={p.id}>{p.po_prod_no} · {p.fabric_name} {p.color_name}</SelectItem>)}</SelectContent></Select>{cwSelected && !f.production_order_id && <p className="mt-1 text-[11px] font-semibold text-rose-600" data-testid="mr-cw-po-required">Production Order wajib untuk Catch Weight.</p>}</div>
        </div>
        <div>
          <div className="mb-2 flex items-center justify-between"><Label className="text-sm font-semibold">Bahan</Label><Button size="sm" variant="outline" disabled={!f.warehouse_id} onClick={() => setItems([...items, { item_id: "", qty: 1 }])}><Plus className="mr-1 h-3.5 w-3.5" /> Bahan</Button></div>
          {!f.warehouse_id ? <p className="text-xs text-slate-400">Pilih Inventory Konveksi dahulu.</p> : items.map((it, i) => {
            const sel = whItems.find((x) => x.id === it.item_id);
            return (
              <div key={i} className="mb-2 grid grid-cols-12 items-center gap-2">
                <div className="col-span-7"><Select value={it.item_id} onValueChange={(v) => pickItem(i, v)}><SelectTrigger data-testid={`mr-item-${i}`}><SelectValue placeholder="Pilih bahan..." /></SelectTrigger><SelectContent>{whItems.map((x) => <SelectItem key={x.id} value={x.id}>{x.name} <span className="text-xs text-slate-400">(stok {numberID(x.available)} {x.unit})</span></SelectItem>)}</SelectContent></Select></div>
                <Input type="number" min="0" className="col-span-3" placeholder="Qty" value={it.qty} onChange={(e) => setItems(items.map((x, idx) => idx === i ? { ...x, qty: e.target.value } : x))} data-testid={`mr-qty-${i}`} />
                <span className="col-span-1 text-xs text-slate-400">{sel?.unit || ""}</span>
                <Button variant="ghost" size="sm" className="col-span-1 text-rose-500" onClick={() => setItems(items.filter((_, idx) => idx !== i))}><X className="h-4 w-4" /></Button>
              </div>
            );
          })}
        </div>
        <div><Label className="text-xs text-slate-500">Catatan</Label><Textarea className="mt-1" rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>
        <p className="text-xs text-slate-400">Stok Konveksi berkurang saat MR "Keluarkan Bahan", bukan saat dibuat.</p>
        <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button><Button onClick={save} disabled={saving || (cwSelected && !f.production_order_id)} data-testid="save-mr-btn" className="bg-indigo-600 hover:bg-indigo-500">{saving ? "..." : "Simpan"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
